import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import SEOHead from "@/components/SEOHead";

const ease = [0.16, 1, 0.3, 1];
const fadeUp = (delay = 0) => ({ initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-50px" }, transition: { duration: 0.6, ease, delay } });

const FAQ_CATEGORIES = [
  {
    category: "The Service",
    items: [
      { q: "What exactly does ApplyStrat do?", a: "We apply to tech jobs on your behalf through official company career portals. We work with you upfront to understand your background, target roles, and preferences — then our team researches relevant openings and submits applications aligned with your approved direction." },
      { q: "How do you apply on my behalf?", a: "After our onboarding session, we set up your application profile with your preferences and role criteria. Our team identifies relevant openings and submits applications through official company portals. We do not use automation shortcuts or mass-apply tools." },
      { q: "What roles do you support?", a: "ML Engineering, AI Engineering, Software Engineering, Cybersecurity, Data Engineering, ML Ops, Cloud & Infrastructure, and related technical paths. We do not support non-technical or generalist roles." },
      { q: "Do you only serve the US market?", a: "Yes. We focus exclusively on the US job market. All candidates must have valid US work authorization." },
    ],
  },
  {
    category: "Process & Expectations",
    items: [
      { q: "Do you guarantee job offers or interviews?", a: "No. We do not guarantee offers, interview calls, or any specific hiring outcomes. Our role is to improve execution quality and save your time." },
      { q: "How long does it take to start getting calls?", a: "Based on prior experience, some candidates begin receiving calls within 6–8 weeks of actively entering the market. Every search is different — timelines depend on your background, target roles, and market conditions." },
      { q: "How many applications per day?", a: "Starter: 20/day. Strategic: 40/day. Elite: unlimited/day. We prioritize quality — applications are reviewed for fit before submission." },
      { q: "How does the weekly feedback loop work?", a: "At the end of each week, we share a summary of applications submitted, any early signals, and our read on what's working. You give feedback and we incorporate it immediately." },
    ],
  },
  {
    category: "Profile & Optimization",
    items: [
      { q: "What does profile optimization include?", a: "Resume review, LinkedIn optimization, cover letter support, and job tracking setup. Included in every plan — not upsells." },
      { q: "Do I need a polished resume before starting?", a: "No. We work with what you have and improve it during onboarding. A stronger baseline helps us start applications faster." },
      { q: "Will you apply to roles I don't want?", a: "No. We work strictly within your approved direction. You provide your target roles, preferred companies, and any restrictions upfront." },
    ],
  },
  {
    category: "Pricing & Refund",
    items: [
      { q: "What are the pricing plans?", a: "Starter ($199/mo), Strategic ($299/mo — full optimization + weekly call), Elite ($499/mo — unlimited + priority support). Month-to-month, no long-term contracts." },
      { q: "What is your refund policy?", a: "Full refund if you're dissatisfied with the strategy we're using for your profile. Not based on hiring outcomes — based purely on strategy satisfaction." },
      { q: "Are there long-term contracts?", a: "No. Plans are month-to-month. Cancel anytime." },
    ],
  },
  {
    category: "Privacy & Permissions",
    items: [
      { q: "How do you handle my personal information?", a: "We handle your information with strict confidentiality. It's only used to apply for positions you've authorized. We never share it with unauthorized parties." },
      { q: "What permission do I give you when I sign up?", a: "You grant broad permission to apply to relevant tech roles based on the preferences you provide during onboarding. You can update or restrict this at any time." },
    ],
  },
];

const FAQPage = () => (
  <div data-testid="faq-page" className="bg-white">
    <SEOHead title="FAQ | ApplyStrat" description="Common questions about ApplyStrat's tech job application service — how we work, our refund policy, what's included, timeline expectations, and eligibility." canonical="https://applystrat.com/faq" />

    <section className="pt-36 pb-16 bg-white text-center" style={{ background: "radial-gradient(circle at 50% -20%, rgba(255,82,0,0.12) 0%, transparent 65%)" }}>
      <div className="max-w-3xl mx-auto px-6">
        <motion.div {...fadeUp()}>
          <p className="section-label-orange mb-4">FAQ</p>
          <h1 className="text-5xl md:text-6xl font-medium tracking-tighter text-ink mb-6">Frequently Asked Questions</h1>
          <p className="text-lg text-ink-secondary max-w-md mx-auto leading-relaxed">Everything you need to know before getting started.</p>
          <a href="mailto:hello@applystrat.com" className="inline-flex items-center gap-2 mt-5 text-brand hover:text-brand-hover text-sm font-medium transition-colors">
            hello@applystrat.com
          </a>
        </motion.div>
      </div>
    </section>

    <section className="py-12 pb-28 bg-white">
      <div className="max-w-3xl mx-auto px-6 space-y-12">
        {FAQ_CATEGORIES.map((cat, catIdx) => (
          <motion.div key={cat.category} {...fadeUp(catIdx * 0.05)}>
            <h2 className="text-ink font-medium text-xl mb-5">{cat.category}</h2>
            <Accordion type="single" collapsible className="space-y-2">
              {cat.items.map((item, i) => (
                <AccordionItem key={i} value={`${cat.category}-${i}`} data-testid={`faq-${cat.category.toLowerCase().replace(/ /g, "-")}-${i}`} className="bg-surface-raised border-0 rounded-2xl px-6 overflow-hidden hover:bg-white hover:shadow-sm transition-all duration-300">
                  <AccordionTrigger className="text-left text-ink font-medium text-sm py-5 hover:no-underline hover:text-brand transition-colors">{item.q}</AccordionTrigger>
                  <AccordionContent className="text-ink-secondary text-sm leading-relaxed pb-5">{item.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        ))}
      </div>
    </section>

    <section className="py-20 bg-surface-raised border-t border-black/5">
      <div className="max-w-2xl mx-auto px-6 text-center">
        <motion.div {...fadeUp()}>
          <h2 className="text-3xl font-medium text-ink mb-4">Still Have Questions?</h2>
          <p className="text-ink-secondary text-base mb-8">Reach out and we'll respond within 24 hours.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="mailto:hello@applystrat.com" data-testid="faq-email-button" className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-surface-raised border border-black/10 text-ink font-medium hover:bg-white hover:shadow-sm transition-all">
              Email Us
            </a>
            <Button asChild size="lg" className="shadow-sm hover:scale-[1.02]">
              <Link to="/apply" data-testid="faq-apply-button">Apply to Work With Us <ArrowRight size={16} /></Link>
            </Button>

          </div>
        </motion.div>
      </div>
    </section>
  </div>
);

export default FAQPage;
